import React from 'react';
import { Navigate, Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, MessageSquare, Globe, Users, LogOut, ArrowLeft, BarChart3 } from 'lucide-react';

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/contacts', icon: MessageSquare, label: 'Contacts' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/dns', icon: Globe, label: 'DNS Monitor' },
    { to: '/admin/users', icon: Users, label: 'Admin Users' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-slate-700">
          <img
            src="https://customer-assets.emergentagent.com/job_file-based-website-1/artifacts/r5rhhf4l_Anlumax-Logos-New-Palette_White.png"
            alt="AnluMAX"
            className="h-8"
          />
          <p className="text-xs text-slate-500 mt-2">Management Platform</p>
        </div>

        <nav className="flex-1 py-4">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
                  isActive ? 'text-blue-400 bg-blue-500/10 border-r-2 border-blue-400' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`
              }
              data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-700 space-y-2">
          <a href="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors px-1">
            <ArrowLeft size={16} /> Back to Site
          </a>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors px-1 w-full" data-testid="logout-btn">
            <LogOut size={16} /> Sign Out
          </button>
          <div className="text-xs text-slate-600 px-1 mt-2">{user.email}</div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
